import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {
  useCompanyDepartmentMaster,
  useInfiniteReports,
  useReportTypes,
} from '../../../core/repositories/reportsRepository';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';

export type FilterItem = {
  reportType: string[];
  department: number[];
  graduationYear: number[];
  sortOrder: 'recent' | 'popular';
};

export type ReportTypeValue = {
  key: string;
  name: string;
};

type Props = {
  companyId: number;
};

export const useHandleStudy = ({companyId}: Props) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const {type, department, graduationYear, order} = router.query;

  const {data: reportTypes, isLoading: isLoadingReportTypes} = useReportTypes(
    {},
  );

  const {
    data: companyDepartmentMaster,
    isLoading: isLoadingCompanyDepartmentMaster,
  } = useCompanyDepartmentMaster({companyId});

  const [graduationYears, setGraduationYears] = useState<number[]>([]);

  const {data: allReports} = useInfiniteReports({
    queryParams: {
      companyIDs: companyId,
      types: [],
      departmentMasterIDs: [],
      graduationYear: [],
      order: 'recent',
      limit: 1000,
    },
    config: {
      enabled: reportTypes?.ok && companyDepartmentMaster?.ok,
    },
  });

  useEffect(() => {
    if (!allReports) {
      return;
    }

    const years = allReports.pages
      .flatMap((page) =>
        page.reports.map((report) => report.reporterGraduationYear),
      )
      .filter(Boolean);

    const uniqueSortedYears = [...new Set(years)].sort((a, b) => b - a);
    setGraduationYears(uniqueSortedYears);
  }, [allReports]);

  const parseQueryParam = (param: string | string[] | undefined): number[] => {
    if (!param) return [];
    if (Array.isArray(param)) return param.map(Number);
    return param.split(',').map(Number);
  };

  const [filterItems, setFilterItems] = useState<FilterItem>({
    reportType: [],
    department: [],
    graduationYear: [],
    sortOrder: 'recent',
  });

  const DISPLAY_ITEM_PER_PAGE = 1000; // 全てのレポートを取得するために十分な数

  // 初期マウント時に空の filterItems で URL を上書きしないよう、初回同期完了まで router.replace をスキップする
  const [isInitialSyncCompleted, setIsInitialSyncCompleted] = useState(false);

  useEffect(() => {
    if (!reportTypes?.ok || !companyDepartmentMaster?.ok) {
      return;
    }

    const orderValue = Array.isArray(order) ? order[0] : order;
    const newFilterItems: FilterItem = {
      reportType: type ? (Array.isArray(type) ? type : type.split(',')) : [],
      department: parseQueryParam(department),
      graduationYear: parseQueryParam(graduationYear),
      sortOrder: orderValue === 'popular' ? 'popular' : 'recent',
    };

    setFilterItems(newFilterItems);
    setIsInitialSyncCompleted(true);
  }, [reportTypes, companyDepartmentMaster]);

  const {data, isFetching, fetchNextPage, isFetchingNextPage} =
    useInfiniteReports({
      queryParams: {
        companyIDs: companyId,
        types: filterItems.reportType,
        departmentMasterIDs:
          filterItems.department && companyDepartmentMaster?.ok
            ? filterItems.department.map((departmentId) => departmentId)
            : [],
        graduationYear: filterItems.graduationYear ?? [],
        order: filterItems.sortOrder || 'recent',
        limit: DISPLAY_ITEM_PER_PAGE,
      },
      config: {
        enabled: reportTypes?.ok && companyDepartmentMaster?.ok,
      },
    });

  const totalCount = data?.pages[0]?.totalCount ?? 0;
  const pageCount = data?.pages.length ?? 1;
  const hasNextPage = totalCount > pageCount * DISPLAY_ITEM_PER_PAGE;

  const loadMoreReports = () => {
    void fetchNextPage();
  };

  const isLoading =
    isLoadingCompanyDepartmentMaster ||
    isLoadingReportTypes ||
    isFetching ||
    isFetchingNextPage;

  // filterItems が変更されたときに URL を更新（WebView と初回同期前はスキップ）
  // biome-ignore lint/correctness/useExhaustiveDependencies: filterItems の変化のみをトリガーにしたいため、他の参照値は意図的に依存配列から除外
  useEffect(() => {
    if (isWebView || !isInitialSyncCompleted) {
      return;
    }
    router
      .replace({
        pathname: router.pathname,
        query: {
          id: companyId,
          type: filterItems.reportType.join(','),
          department: filterItems.department.join(','),
          graduationYear: filterItems.graduationYear.join(','),
          order: filterItems.sortOrder,
        },
      })
      .catch((error) => {
        console.error('Failed to push the new URL', error);
      });
  }, [filterItems]);

  return {
    reportTypes: reportTypes?.ok ? reportTypes.value.types : [],
    companyDepartmentMaster,
    graduationYears,
    reports: data,
    isLoading,
    filterItems,
    setFilterItems,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    loadMoreReports,
  };
};
